import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ErroreApi, chiama } from '../api/cliente.js';
import { locale, t } from '../i18n/lingua.js';

/**
 * Le azioni di massa di un elenco.
 *
 * Nel v2 ogni elenco ha SELEZIONA, ELIMINA e ARCHIVIA in fondo. Servono
 * davvero — capita di dover togliere cinquanta o sessanta ordini in una volta
 * — e sono le stesse ovunque, quindi questa barra è una sola per tutti gli
 * elenchi.
 *
 * Tre cose deliberate:
 *
 * **Eliminare chiede conferma, archiviare no.** Archiviare si annulla con un
 * clic; eliminare sessanta righe no. La conferma dice quante sono, perché il
 * numero è l'unica cosa che si controlla davvero prima di premere.
 *
 * **L'esito si legge.** Se di sessanta ordini otto non si sono potuti
 * eliminare, la barra lo dice e li elenca. Riuscire in silenzio a metà è il
 * modo migliore per far credere a qualcuno di aver finito.
 *
 * **La selezione resta finché non è chiaro cosa è successo.** Si svuota solo
 * quando tutto è andato: se qualcosa è rimasto indietro, resta selezionato e
 * si vede.
 *
 * Certi elenchi ne hanno di proprie — gli ordini hanno «Invia al vettore» e
 * «Stato 2» — e arrivano da fuori con `azioni`: la barra non sa cosa facciano,
 * sa solo che rispondono nella stessa forma.
 */

export interface Scarto {
  id: number;
  reason: string;
}

export interface AzioneExtra {
  /** Il testo del pulsante. */
  etichetta: string;
  /** Dove si manda: `/api/orders/bulk/send-to-carrier`. */
  percorso: string;
  /** Cosa si chiede prima di partire. Senza, si parte e basta. */
  conferma?: string;
  /** Una spunta nella conferma, che aggiunge un campo al corpo della richiesta. */
  opzione?: { campo: string; etichetta: string };
  /** Quante righe per volta si possono mandare. */
  massimo?: number;
  /** Come si dice l'esito riuscito: «12 ordini mandati al vettore». */
  esito: (quante: number) => string;
}

type Conferma = { tipo: 'delete' } | { tipo: 'extra'; azione: AzioneExtra } | null;

/**
 * Il singolare, per la conferma di una riga sola. Togliere la «i» finale
 * andava per «ordini», non per «listini» (diventava «listine»), «documenti» o
 * «trattative».
 */
const NOMI: Record<string, { uno: string; tanti: string }> = {
  anagrafiche: { uno: t('anagrafica'), tanti: t('anagrafiche') },
  articoli: { uno: t('articolo'), tanti: t('articoli') },
  documenti: { uno: t('documento'), tanti: t('documenti') },
  listini: { uno: t('listino'), tanti: t('listini') },
  ordini: { uno: t('ordine'), tanti: t('ordini') },
  trattative: { uno: t('trattativa'), tanti: t('trattative') },
};

export function AzioniDiMassa({
  percorso,
  nome,
  selezione,
  onFatto,
  onAnnulla,
  conElimina = false,
  conRipristina = false,
  azioni = [],
  chiaviDaRileggere,
}: {
  /** Il percorso delle azioni: `/api/orders/bulk`. */
  percorso: string;
  /** Come si chiamano queste righe al plurale: «ordini». */
  nome: string;
  selezione: Set<string | number>;
  onFatto: () => void;
  onAnnulla: () => void;
  /** Vero se queste righe si possono anche eliminare, non solo archiviare. */
  conElimina?: boolean;
  /** Vero quando si stanno guardando anche le archiviate. */
  conRipristina?: boolean;
  /** Le azioni che ha solo questo elenco. */
  azioni?: AzioneExtra[];
  /** Le query da rileggere dopo: l'elenco e i suoi conteggi. */
  chiaviDaRileggere: string[];
}) {
  const [conferma, setConferma] = useState<Conferma>(null);
  const [opzioneAttiva, setOpzioneAttiva] = useState(false);
  const [esito, setEsito] = useState<{ done: number; skipped: Scarto[]; dice?: (n: number) => string } | null>(
    null,
  );
  const client = useQueryClient();

  const azione = useMutation({
    mutationFn: ({
      percorso: dove,
      corpo,
    }: {
      percorso: string;
      corpo: Record<string, unknown>;
      dice?: (n: number) => string;
    }) =>
      chiama<{ done: number; skipped: Scarto[] }>(dove, {
        metodo: 'POST',
        corpo: { ids: [...selezione].map(Number), ...corpo },
      }),
    onSuccess: (risultato, variabili) => {
      setConferma(null);
      setOpzioneAttiva(false);
      setEsito({ ...risultato, dice: variabili.dice });
      for (const chiave of chiaviDaRileggere) {
        void client.invalidateQueries({ queryKey: [chiave] });
      }
      // la selezione si svuota solo se non è rimasto indietro niente
      if (risultato.skipped.length === 0) onFatto();
    },
  });

  const quante = selezione.size;

  /**
   * L'esito resta a video anche quando la selezione si svuota: le righe
   * archiviate spariscono dall'elenco, e prima la barra spariva con loro
   * prima che si potesse leggere «N righe sistemate». Se è andato tutto
   * bene si chiude da solo; se qualcosa è rimasto indietro, aspetta «Chiudi».
   */
  useEffect(() => {
    if (!esito || esito.skipped.length > 0) return;
    const timer = setTimeout(() => setEsito(null), 8000);
    return () => clearTimeout(timer);
  }, [esito]);
  /**
   * La conferma vale per la selezione che c'era quando si è chiesta: se la
   * selezione si svuota (cambio pagina, «Annulla selezione») la conferma si
   * chiude. Prima restava «Elimino 0 documenti?» con «Sì, elimina».
   */
  useEffect(() => {
    if (quante === 0) setConferma(null);
  }, [quante]);

  // Esc chiude la conferma come chiude le finestre; mentre l'azione va, no
  useEffect(() => {
    if (!conferma) return;
    const tasto = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !azione.isPending) setConferma(null);
    };
    window.addEventListener('keydown', tasto);
    return () => window.removeEventListener('keydown', tasto);
  }, [conferma, azione.isPending]);

  const errore = azione.error instanceof ErroreApi ? azione.error.message : null;

  const verbo =(action: 'archive' | 'restore' | 'delete') =>
    azione.mutate({ percorso, corpo: { action } });

  const lancia = (extra: AzioneExtra) =>
    azione.mutate({
      percorso: extra.percorso,
      corpo: extra.opzione && opzioneAttiva ? { [extra.opzione.campo]: true } : {},
      dice: extra.esito,
    });

  if (!esito && quante === 0) return null;

  if (esito) {
    return (
      <div className="bulkbar" role="status">
        <strong>
          {esito.done === 0
            ? t('Non è stato toccato niente.')
            : esito.dice
              ? esito.dice(esito.done)
              : esito.done === 1
                ? t('1 riga sistemata.')
                : t('{n} righe sistemate.', { n: esito.done.toLocaleString(locale()) })}
        </strong>
        {esito.skipped.length > 0 && (
          <span className="scarti">
            {esito.skipped.length === 1
              ? t('1 è rimasta indietro:')
              : t('{n} sono rimaste indietro:', { n: esito.skipped.length })}{' '}
            {/* i motivi si mostrano tutti fino a cinque: oltre, il numero
                basta e l'elenco diventerebbe un muro di testo */}
            {esito.skipped
              .slice(0, 5)
              .map((s) => s.reason)
              .join(' · ')}
            {esito.skipped.length > 5 && ` e altre ${esito.skipped.length - 5}`}
          </span>
        )}
        <span style={{ marginLeft: 'auto' }} />
        <button
          className="btn"
          onClick={() => {
            setEsito(null);
            onFatto();
          }}
        >
          {t('Chiudi')}
        </button>
      </div>
    );
  }

  if (conferma?.tipo === 'delete') {
    return (
      <div className="bulkbar bulkbar-attenzione" role="alertdialog">
        <span className="avviso-segno">▲</span>
        <strong>
          {t('Elimino {n} {cosa}?', {
            n: quante.toLocaleString(locale()),
            cosa: quante === 1 ? (NOMI[nome]?.uno ?? nome) : (NOMI[nome]?.tanti ?? nome),
          })}
        </strong>
        <span className="dim">
          {t('Escono dagli elenchi e dalle ricerche. Quello che ci sta attaccato — righe, documenti, storico — resta dov’è.')}
        </span>
        <span style={{ marginLeft: 'auto' }} />
        <button className="btn" onClick={() => setConferma(null)} disabled={azione.isPending}>
          {t('Annulla')}
        </button>
        <button
          className="btn btn-pericolo"
          onClick={() => verbo('delete')}
          disabled={azione.isPending}
        >
          {azione.isPending ? t('Elimino…') : t('Sì, elimina')}
        </button>
      </div>
    );
  }

  if (conferma?.tipo === 'extra') {
    const extra = conferma.azione;
    return (
      <div className="bulkbar bulkbar-attenzione" role="alertdialog">
        <strong>{extra.conferma}</strong>
        {extra.opzione && (
          <label className="dim" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={opzioneAttiva}
              onChange={(e) => setOpzioneAttiva(e.target.checked)}
            />
            {extra.opzione.etichetta}
          </label>
        )}
        <span style={{ marginLeft: 'auto' }} />
        <button
          className="btn"
          onClick={() => {
            setConferma(null);
            setOpzioneAttiva(false);
          }}
          disabled={azione.isPending}
        >
          {t('Annulla')}
        </button>
        <button className="btn" onClick={() => lancia(extra)} disabled={azione.isPending}>
          {azione.isPending ? t('Un momento…') : t('Conferma')}
        </button>
      </div>
    );
  }

  return (
    <div className="bulkbar">
      <strong>
        {quante === 1 ? t('1 riga selezionata') : t('{n} righe selezionate', { n: quante.toLocaleString(locale()) })}
      </strong>
      {errore && (
        <span className="dim" style={{ color: 'var(--tomato)' }}>
          {errore}
        </span>
      )}
      <span className="btn-sep" />
      <button className="btn" onClick={() => verbo('archive')} disabled={azione.isPending}>
        {t('Archivia')}
      </button>
      {conRipristina && (
        <button className="btn" onClick={() => verbo('restore')} disabled={azione.isPending}>
          {t('Ripristina')}
        </button>
      )}
      {conElimina && (
        <button
          className="btn"
          onClick={() => setConferma({ tipo: 'delete' })}
          disabled={azione.isPending}
        >
          {t('Elimina')}
        </button>
      )}
      {azioni.length > 0 && <span className="btn-sep" />}
      {azioni.map((extra) => {
        /**
         * Oltre il tetto il pulsante non parte e dice perché. Lasciarlo
         * premere per farsi rispondere «al massimo cento» dal servizio è un
         * giro inutile: il numero si sa già di qua.
         */
        const troppe = extra.massimo !== undefined && quante > extra.massimo;
        return (
          <button
            key={extra.percorso}
            className="btn"
            title={troppe ? t('Al massimo {n} per volta.', { n: extra.massimo ?? '' }) : undefined}
            onClick={() =>
              extra.conferma ? setConferma({ tipo: 'extra', azione: extra }) : lancia(extra)
            }
            disabled={azione.isPending || troppe}
          >
            {extra.etichetta}
          </button>
        );
      })}
      <span style={{ marginLeft: 'auto' }} />
      <button className="btn btn-ghost" onClick={onAnnulla}>
        {t('Annulla selezione')}
      </button>
    </div>
  );
}
